import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth } from '../services/api';
import { toast } from 'react-hot-toast';

const ConfirmRegistration: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(0);

  // Обратный отсчет для повторной отправки кода
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !code.trim()) {
      setError('Заполните все поля');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await auth.confirmRegistration({ email, code });
      
      // Сохраняем токены
      sessionStorage.setItem('token', response.data.token);
      sessionStorage.setItem('refreshToken', response.data.refreshToken);
      
      toast.success('Регистрация успешно подтверждена!');
      navigate('/');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Ошибка при подтверждении регистрации';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!email.trim()) {
      setError('Введите email');
      return;
    }

    setResendLoading(true);
    setError(null);

    try {
      await auth.resendRegistrationCode({ email });
      setCountdown(120); // 2 минуты
      toast.success('Код отправлен на ваш email');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Ошибка при отправке кода';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Подтверждение регистрации
        </Typography>

        <Typography variant="body1" sx={{ mb: 3, textAlign: 'center' }}>
          На ваш email отправлен код подтверждения. Введите его ниже для завершения регистрации.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleConfirm} noValidate>
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email"
            name="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            id="code"
            label="Код подтверждения"
            name="code"
            autoComplete="off"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            disabled={loading}
            inputProps={{ maxLength: 6 }}
            helperText="Введите 6-значный код из письма"
          />
          
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading || !email.trim() || !code.trim()}
            sx={{ mt: 2 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Подтвердить регистрацию'}
          </Button>

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button
              variant="text"
              onClick={handleResendCode}
              disabled={resendLoading || countdown > 0 || !email.trim()}
              sx={{ textTransform: 'none' }}
            >
              {resendLoading ? (
                <CircularProgress size={20} />
              ) : countdown > 0 ? (
                `Отправить код повторно (${countdown}s)`
              ) : (
                'Отправить код повторно'
              )}
            </Button>
          </Box>

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Link
              component="button"
              variant="body2"
              onClick={() => navigate('/login')}
              sx={{ textDecoration: 'none' }}
            >
              Вернуться к входу
            </Link>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default ConfirmRegistration; 